import { streemLogger as _streemLogger } from '../util/streem_logger';
import { AWS_ATTENDEE_PREFIX } from '../util/constants';
import { LowBandwidthMetricsObserver } from '../observers/low_bandwidth_metrics_observer';
import { BandwidthLevel } from '../observers/bandwidth_level';
import { addToArrayKeepingMostRecentItems } from '../util/array_helpers';
export const LOW_BANDWIDTH_TOPIC_PREFIX = 'low-bw-';
// we want to send low bandwidth/bandwidth normal messages more than once in case the remote user
//      joins the call after the first message is sent.  However, we don't want to send them too
//      frequently.  These are the minimum intervals between messages.
export const LOW_BANDWIDTH_MESSAGE_INTERVAL_MS = 1950; // 2 seconds with a little buffer
export const NORMAL_BANDWIDTH_MESSAGE_INTERVAL_MS = 4950; // 5 seconds with a little buffer
export const LOW_BANDWIDTH_MESSAGE_CONTENTS = 'low bandwidth detected';
export const BANDWIDTH_NORMAL_MESSAGE_CONTENTS = 'bandwidth normal';
// see: https://aws.github.io/amazon-chime-sdk-js/modules/qualitybandwidth_connectivity.html#adjust-local-video-quality
export const MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS = 500; // minimum for 540p
export const DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS = 1400; // this is the default value in the SDK
export const MAX_BANDWIDTH_LEVEL_HISTORY_LENGTH = 5;
export const LOW_BANDWIDTH_PERIOD_THRESHOLD = 2; // number of consecutive low bandwidth levels to trigger low bandwidth
export const NORMAL_BANDWIDTH_PERIOD_THRESHOLD = 3; // number of consecutive normal bandwidth levels to trigger normal bandwidth message
export class LowBandwidthManager {
    constructor({ audioVideo, attendeeId, externalUserIdForLocalUser, localVideoTrackEnabledObservable, remoteVideoTrackEnabledObservable, chimeConnectionObserver: chimeObserver, lowBandwidthOptimizationEnabled, }) {
        this.remoteUsers = {};
        this.lastLowBandwidthMessageTime = Date.now() - LOW_BANDWIDTH_MESSAGE_INTERVAL_MS;
        this.lastBandwidthNormalMessageTime = Date.now() - NORMAL_BANDWIDTH_MESSAGE_INTERVAL_MS;
        this.currentMaxVideoBandwidthKbps = DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS;
        this.lowBandwidthDetectedLocally = false;
        this.logger = _streemLogger.extend('sdk-chime-video:LowBandwidthManager');
        this.localBandwidthLevels = [];
        this.onLocalBandwidthUpdated = (bandwidthLevel) => {
            this.updateBandwidthLevels(bandwidthLevel);
            if (this.localBandwidthLow()) {
                this.onLocalBandwidthLow();
                return;
            }
            if (this.localBandwidthNormal()) {
                this.onLocalBandwidthNormal();
                return;
            }
        };
        this.sendLowBandwidthMessage = () => {
            this.logger.info('Sending low bandwidth message');
            const message = new TextEncoder().encode(LOW_BANDWIDTH_MESSAGE_CONTENTS);
            this.audioVideo.realtimeSendDataMessage(this.bandwidthTopicForLocalUser, message);
        };
        this.onLocalBandwidthLow = () => {
            this.lowBandwidthDetectedLocally = true;
            if (!this.lowBandwidthOptimizationEnabled) {
                this.logger.info('Low bandwidth detected, but Low Bandwidth Optimization is disabled, so not sending message');
                return;
            }
            this.performLowBandwidthUpdates();
            const now = Date.now();
            if (now - this.lastLowBandwidthMessageTime < LOW_BANDWIDTH_MESSAGE_INTERVAL_MS) {
                this.logger.info('Not sending low bandwidth message because it is too soon');
                return;
            }
            this.lastLowBandwidthMessageTime = now;
            this.sendLowBandwidthMessage();
        };
        this.onLocalBandwidthNormal = () => {
            this.lowBandwidthDetectedLocally = false;
            if (!this.lowBandwidthOptimizationEnabled) {
                this.logger.debug('Bandwidth normal, but Low Bandwidth Optimization is disabled, so not sending message');
                return;
            }
            this.performBandwidthNormalUpdates();
            const now = Date.now();
            if (now - this.lastBandwidthNormalMessageTime < NORMAL_BANDWIDTH_MESSAGE_INTERVAL_MS) {
                this.logger.debug('Not sending bandwidth normal message because it is too soon');
                return;
            }
            this.lastBandwidthNormalMessageTime = now;
            this.sendBandwidthNormalMessage();
        };
        this.sendBandwidthNormalMessage = () => {
            this.logger.info('Sending bandwidth normal message');
            const message = new TextEncoder().encode(BANDWIDTH_NORMAL_MESSAGE_CONTENTS);
            this.audioVideo.realtimeSendDataMessage(this.bandwidthTopicForLocalUser, message);
        };
        this.attendeeIdPresenceCallback = async (_, // attendeeId
        present, externalUserId) => {
            if (!externalUserId ||
                externalUserId.startsWith(AWS_ATTENDEE_PREFIX) ||
                externalUserId === this.externalUserIdForLocalUser) {
                return;
            }
            if (present) {
                this.subscribeToLowBandwidthMessageForUser(externalUserId);
            }
            else {
                this.unsubscribeFromLowBandwidthMessageForUser(externalUserId);
            }
        };
        this.audioVideo = audioVideo;
        this.attendeeId = attendeeId;
        this.externalUserIdForLocalUser = externalUserIdForLocalUser;
        this.localVideoTrackEnabledObservable = localVideoTrackEnabledObservable;
        this.remoteVideoTrackEnabledObservable = remoteVideoTrackEnabledObservable;
        this.chimeConnectionObserver = chimeObserver;
        this.lowBandwidthOptimizationEnabled = lowBandwidthOptimizationEnabled;
        this.bandwidthTopicForLocalUser = this.messageTopicForUser(this.externalUserIdForLocalUser);
        this.audioVideo.realtimeSubscribeToAttendeeIdPresence(this.attendeeIdPresenceCallback);
        this.lowBandwidthMetricsObserver = new LowBandwidthMetricsObserver(this.attendeeId, this.localVideoTrackEnabledObservable, this.remoteVideoTrackEnabledObservable, this.onLocalBandwidthUpdated, this.logger);
        this.chimeConnectionObserver.addMetricsObserver(this.lowBandwidthMetricsObserver.metricsDidReceive);
    }
    disconnect() {
        Object.values(this.remoteUsers).forEach(({ topic }) => {
            this.audioVideo.realtimeUnsubscribeFromReceiveDataMessage(topic);
        });
        this.remoteUsers = {};
        this.audioVideo.realtimeUnsubscribeToAttendeeIdPresence(this.attendeeIdPresenceCallback);
        this.chimeConnectionObserver.removeMetricsObserver(this.lowBandwidthMetricsObserver.metricsDidReceive);
        this.lowBandwidthMetricsObserver.disconnect();
    }
    onRemoteMessageReceived(remoteExternalUserId) {
        return (message) => {
            const messageContents = new TextDecoder().decode(message.data);
            if (!this.lowBandwidthOptimizationEnabled) {
                this.logger.warn(`Unexpected condition: Received message: ${messageContents} from remote user: ${remoteExternalUserId} even though Low Bandwidth Optimization was disabled`);
                return;
            }
            if (messageContents === LOW_BANDWIDTH_MESSAGE_CONTENTS) {
                this.logger.info(`Received low bandwidth message from userId: ${remoteExternalUserId}`);
                this.remoteUsers[remoteExternalUserId].lowBandwidthDetected = true;
                this.performLowBandwidthUpdates();
            }
            else if (messageContents === BANDWIDTH_NORMAL_MESSAGE_CONTENTS) {
                this.logger.info(`Received bandwidth normal message from userId: ${remoteExternalUserId}`);
                this.remoteUsers[remoteExternalUserId].lowBandwidthDetected = false;
                this.performBandwidthNormalUpdates();
            }
            else {
                this.logger.error(`Unexpected message received from userId: ${remoteExternalUserId}, message: "${messageContents}"`);
            }
        };
    }
    subscribeToLowBandwidthMessageForUser(remoteExternalUserId) {
        if (Object.keys(this.remoteUsers).includes(remoteExternalUserId)) {
            return;
        }
        const topic = this.messageTopicForUser(remoteExternalUserId);
        this.remoteUsers[remoteExternalUserId] = {
            topic,
            lowBandwidthDetected: false,
        };
        this.audioVideo.realtimeSubscribeToReceiveDataMessage(topic, this.onRemoteMessageReceived(remoteExternalUserId));
    }
    unsubscribeFromLowBandwidthMessageForUser(remoteExternalUserId) {
        if (!Object.keys(this.remoteUsers).includes(remoteExternalUserId)) {
            return;
        }
        this.audioVideo.realtimeUnsubscribeFromReceiveDataMessage(this.remoteUsers[remoteExternalUserId].topic);
        delete this.remoteUsers[remoteExternalUserId];
    }
    messageTopicForUser(externalUserId) {
        return `${LOW_BANDWIDTH_TOPIC_PREFIX}${externalUserId}`;
    }
    // Note that this may be called when low bandwidth is detected locally (onLowBandwidthDetectedLocally) or when a low bandwidth
    //    message is received from a remote user(onRemoteMessageReceived)
    performLowBandwidthUpdates() {
        if (this.currentMaxVideoBandwidthKbps !== MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS) {
            this.logger.info(`Low bandwidth detected, lowering bandwidth to ${MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS}`);
            this.audioVideo.setVideoMaxBandwidthKbps(MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS);
            this.currentMaxVideoBandwidthKbps = MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS;
        }
        else {
            this.logger.debug(`Low Bandwidth detected, but not updating bandwidth because it is already at the minimum value: ${MINIMUM_MAX_VIDEO_BANDWIDTH_KBPS}`);
        }
    }
    // Note that this may be called when bandwidth is normal locally (onBandwidthNormalLocally) or when a bandwidth normal
    //   message is received from a remote user (onRemoteMessageReceived)
    performBandwidthNormalUpdates() {
        // ensure bandwidth is okay for all users (local and remote) before updating
        if (!this.shouldPerformBandwidthNormalUpdates()) {
            this.logger.debug('Bandwidth normal for some users, but not updating local video bandwidth because not all users have normal bandwidth');
            return;
        }
        if (this.currentMaxVideoBandwidthKbps === DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS) {
            this.logger.debug(`Bandwidth normal, but not updating bandwidth because it is already at the default value: ${DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS}`);
            return;
        }
        this.logger.info(`Bandwidth normal, updating bandwidth to default value: ${DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS}`);
        this.audioVideo.setVideoMaxBandwidthKbps(DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS);
        this.currentMaxVideoBandwidthKbps = DEFAULT_MAX_VIDEO_BANDWIDTH_KBPS;
    }
    shouldPerformBandwidthNormalUpdates() {
        const someRemoteUsersHaveLowBandwidth = Object.values(this.remoteUsers).some(({ lowBandwidthDetected }) => lowBandwidthDetected);
        return !this.lowBandwidthDetectedLocally && !someRemoteUsersHaveLowBandwidth;
    }
    localBandwidthLow() {
        // returns true if the last LOW_BANDWIDTH_PERIOD_THRESHOLD consecutive low bandwidth levels are low
        if (this.localBandwidthLevels.length < LOW_BANDWIDTH_PERIOD_THRESHOLD) {
            return false;
        }
        for (let i = 0; i < LOW_BANDWIDTH_PERIOD_THRESHOLD; i++) {
            if (this.localBandwidthLevels[i] !== BandwidthLevel.LOW) {
                return false;
            }
        }
        return true;
    }
    localBandwidthNormal() {
        // returns true if the last NORMAL_BANDWIDTH_PERIOD_THRESHOLD bandwidth levels are normal
        if (this.localBandwidthLevels.length < NORMAL_BANDWIDTH_PERIOD_THRESHOLD) {
            return false;
        }
        for (let i = 0; i < NORMAL_BANDWIDTH_PERIOD_THRESHOLD; i++) {
            if (this.localBandwidthLevels[i] !== BandwidthLevel.NORMAL) {
                return false;
            }
        }
        return true;
    }
    updateBandwidthLevels(bandwidthLevel) {
        addToArrayKeepingMostRecentItems(this.localBandwidthLevels, bandwidthLevel, MAX_BANDWIDTH_LEVEL_HISTORY_LENGTH);
    }
}
//# sourceMappingURL=low_bandwidth_manager.js.map